import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavbar from '../components/TopNavbar';
import UserDataView from '../components/UserDataView';
import Footer from '../components/Footer';
import { FaDownload, FaSpinner } from 'react-icons/fa';

const UserView = ({ userInfo, setIsAuthenticated }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeStatement, setActiveStatement] = useState(null);
  const [showDownloadIcon, setShowDownloadIcon] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState('');

  // If somehow admin reaches here, redirect to dashboard
  useEffect(() => {
    if (userInfo?.role === 'admin' || userInfo?.role === 'superadmin') {
      navigate('/dashboard');
    }
  }, [userInfo, navigate]);

  /* ---------------- LISTEN FOR STATEMENT CHANGES FROM UserDataView ---------------- */
  useEffect(() => {
    const handleStatementChange = (event) => {
      if (event.detail && event.detail.statementType && event.detail.company) {
        console.log('Statement change detected:', {
          statement: event.detail.statementType,
          company: event.detail.company.name
        });
        
        setActiveStatement(event.detail.statementType);
        setCompanyName(event.detail.company.name);
        setShowDownloadIcon(true);
        
        if (!selectedCompany || selectedCompany.id !== event.detail.company.id) {
          setSelectedCompany(event.detail.company);
        }
      }
    };

    window.addEventListener('userDataViewStatementChanged', handleStatementChange);

    return () => {
      window.removeEventListener('userDataViewStatementChanged', handleStatementChange);
    };
  }, [selectedCompany]);

  /* ---------------- SHOW DOWNLOAD ICON WHEN COMPANY IS SELECTED ---------------- */
  useEffect(() => {
    // Show download icon immediately if a company is selected
    if (selectedCompany) {
      setShowDownloadIcon(true);
      setCompanyName(selectedCompany.name);
      
      // Set default statement if not already set
      if (!activeStatement) {
        const defaultStatement = selectedCompany.hasIncomeStatement ? "Income Statement" :
                               selectedCompany.hasBalanceSheet ? "Balance Sheet" :
                               selectedCompany.hasCashFlow ? "Cash Flow" : "Income Statement";
        setActiveStatement(defaultStatement);
      }
    } else {
      setShowDownloadIcon(false);
      setCompanyName('');
    }
  }, [selectedCompany]);

  /* ---------------- HANDLE LOGOUT ---------------- */
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('userInfo');
    setIsAuthenticated(false);
    navigate('/login', { replace: true });
  };

  /* ---------------- HANDLE COMPANY SELECTION ---------------- */
  const handleCompanySelect = (company) => {
    console.log('Company selected in UserView:', company);
    setSelectedCompany(company);
    setCompanyName(company.name);
    setSearchQuery(`${company.name} (${company.ticker || ''})`);
    
    // Show download icon immediately when company is selected
    setShowDownloadIcon(true);
    
    // Set active statement based on available data
    if (company.hasIncomeStatement) {
      setActiveStatement("Income Statement");
    } else if (company.hasBalanceSheet) {
      setActiveStatement("Balance Sheet");
    } else if (company.hasCashFlow) {
      setActiveStatement("Cash Flow");
    } else {
      setActiveStatement("Income Statement"); // Default fallback
    }
  };

  /* ---------------- HANDLE HOME CLICK ---------------- */
  const handleHomeClick = () => {
    setSelectedCompany(null);
    setSearchQuery('');
    setActiveStatement(null);
    setShowDownloadIcon(false);
    setCompanyName('');
    setIsDownloading(false);
    setDownloadStatus('');
  };

  /* ---------------- HANDLE DOWNLOAD CLICK ---------------- */
  const handleDownloadClick = async () => {
    console.log('Download button clicked:', {
      company: companyName,
      statement: activeStatement,
      hasDownloadFunction: !!window.handleUserDataDownload
    });
    
    // Check if we have the necessary data
    if (!selectedCompany || !activeStatement) {
      alert('Please select a company and statement first.');
      return;
    }
    
    // Check if download function is available
    if (!window.handleUserDataDownload) {
      console.warn('Download function not available yet, trying direct download...');
      
      // Try direct download as fallback
      tryDirectDownload();
      return;
    }
    
    setIsDownloading(true);
    setDownloadStatus(`Downloading ${activeStatement}...`);
    
    try {
      // Create a promise wrapper for the download function
      const downloadPromise = new Promise((resolve, reject) => {
        const originalDownloadFunction = window.handleUserDataDownload;
        
        // Create a wrapper function
        window.handleUserDataDownload = async () => {
          try {
            await originalDownloadFunction();
            resolve(true);
          } catch (error) {
            reject(error);
          }
        };
        
        // Trigger the download
        window.handleUserDataDownload();
      });
      
      // Wait for download to complete
      await downloadPromise;
      
      setDownloadStatus('Download complete!');
      setTimeout(() => setDownloadStatus(''), 3000);
      
    } catch (error) {
      console.error('Download failed:', error);
      setDownloadStatus('Download failed');
      setTimeout(() => setDownloadStatus(''), 3000);
      
      // Show error message
      let errorMessage = 'Failed to download file. ';
      if (error.message.includes('404')) {
        errorMessage += 'File not found on server.';
      } else if (error.message.includes('403')) {
        errorMessage += 'No permission to download.';
      } else if (error.message.includes('401')) {
        errorMessage += 'Please login again.';
      } else {
        errorMessage += error.message;
      }
      
      alert(errorMessage);
      
      // Try alternative download method
      tryDirectDownload();
    } finally {
      setIsDownloading(false);
    }
  };

  /* ---------------- DIRECT DOWNLOAD METHOD ---------------- */
  const tryDirectDownload = async () => {
    if (!selectedCompany || !activeStatement) return;
    
    setIsDownloading(true);
    setDownloadStatus('Trying direct download...');
    
    try {
      // Get the statement ID for download
      const searchResponse = await fetch(`${window.API_BASE_URL || '/api/v1'}/excel/search?company_name=${encodeURIComponent(selectedCompany.name)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!searchResponse.ok) {
        throw new Error(`Search failed: ${searchResponse.status}`);
      }
      
      const searchData = await searchResponse.json();
      
      if (!searchData.files || searchData.files.length === 0) {
        throw new Error('Company not found');
      }
      
      const companyData = searchData.files[0];
      const statementTypeMap = {
        'Income Statement': 'income_statement',
        'Balance Sheet': 'balance_sheet',
        'Cash Flow': 'cash_flow'
      };
      
      const backendType = statementTypeMap[activeStatement];
      const statementInfo = companyData.statement_data?.[backendType];
      
      if (!statementInfo || !statementInfo.statement_id) {
        throw new Error(`No ${activeStatement} data available for download`);
      }
      
      // Try to download
      const downloadUrl = `${window.API_BASE_URL || '/api/v1'}/excel/statement/${statementInfo.statement_id}/download`;
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const safeCompanyName = selectedCompany.name.replace(/[^a-zA-Z0-9]/g, '_');
      const safeStatement = activeStatement.replace(/\s+/g, '_');
      link.download = `${safeCompanyName}_${safeStatement}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      window.URL.revokeObjectURL(url);
      
      setDownloadStatus('Direct download complete!');
      setTimeout(() => setDownloadStatus(''), 3000);
      
    } catch (error) {
      console.error('Direct download failed:', error);
      setDownloadStatus('Direct download failed');
      setTimeout(() => setDownloadStatus(''), 3000);
      
      alert(`Direct download failed: ${error.message}`);
      
      // Last resort: Open new tab with common download patterns
      const baseUrl = window.location.origin;
      const statementMap = {
        'Income Statement': 'income_statement',
        'Balance Sheet': 'balance_sheet',
        'Cash Flow': 'cash_flow'
      };
      
      const downloadUrls = [
        `${baseUrl}/api/v1/excel/download/${statementMap[activeStatement]}/${selectedCompany.id}`,
        `${baseUrl}/api/v1/excel/company/${selectedCompany.name}/download`,
        `${baseUrl}/api/v1/excel/${statementMap[activeStatement]}/${selectedCompany.id}/download`
      ];
      
      window.open(downloadUrls[0], '_blank');
      alert('Opening download in new tab. If download doesn\'t start, please contact support.');
      
    } finally {
      setIsDownloading(false);
    }
  };

  /* ---------------- CUSTOM TOP NAVBAR WITH DOWNLOAD ICON ---------------- */
  const CustomTopNavbar = () => {
    return (
      <div className="relative w-full">
        <TopNavbar 
          sidebarOpen={false}
          setSidebarOpen={() => {}}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleLogout={handleLogout}
          onCompanySelect={handleCompanySelect}
          fullscreenMode={true}
          onHomeClick={handleHomeClick}
          activeSection="data"
          userRole="user"
          currentCompany={selectedCompany}
          activeStatement={activeStatement}
          financialData={null}
        />
        
        {/* Download Icon - ALWAYS VISIBLE WHEN COMPANY IS SELECTED */}
        {showDownloadIcon && selectedCompany && (
          <div className="absolute top-4 right-24 z-50 flex items-center gap-2">
            {/* File info badge - Show which statement will be downloaded */}
            {activeStatement && (
              <div className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs px-3 py-1 rounded-md">
                {companyName} - {activeStatement}
              </div>
            )}
            
            {/* Download Status */}
            {downloadStatus && (
              <div className={`text-xs px-2 py-1 rounded ${
                downloadStatus.includes('complete') || downloadStatus.includes('success') 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
              }`}>
                {downloadStatus}
              </div>
            )}
            
            {/* Download button */}
            <button
              onClick={handleDownloadClick}
              disabled={isDownloading || !activeStatement}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg transition-all duration-200 
                       ${isDownloading 
                         ? 'bg-gray-400 cursor-not-allowed' 
                         : !activeStatement
                         ? 'bg-gray-500 cursor-not-allowed'
                         : 'bg-green-600 hover:bg-green-700 transform hover:scale-105 active:scale-95'
                       } text-white`}
              title={activeStatement ? `Download ${activeStatement} for ${companyName}` : 'Select a statement to download'}
            >
              {isDownloading ? (
                <>
                  <FaSpinner className="text-lg animate-spin" />
                  <span className="font-medium">Downloading...</span>
                </>
              ) : (
                <>
                  <FaDownload className="text-lg" />
                  <span className="font-medium">
                    {!activeStatement ? 'Select Statement' : 'Download'}
                  </span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Fixed Top Navigation with Download Icon */}
      <div className="fixed top-0 left-0 right-0 z-40">
        <CustomTopNavbar />
      </div>
      
      {/* Main Content Area */}
      <div className="flex-1 pt-16">
        <main className="h-full">
          <UserDataView
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedCompany={selectedCompany}
            onCompanySelect={handleCompanySelect}
          />
        </main>
      </div>
      
      {/* Footer - Only show when no company is selected (home view) */}
      {!selectedCompany && <Footer />}
    </div>
  );
};

export default UserView;